import { useEffect, useState, useRef } from 'react';

import { useUtils } from '../../../../core/hooks/useUtils';
import {
	MODAL_TYPES,
	useGlobalModalContext,
} from '../../../../shared/global-modal';
import { getCommentsAction } from '../../../../store/Comments/action';
import { GET_COMMENTS } from '../../../../store/Comments/types';
import {
	getMembersAction,
	inviteMemberAction,
} from '../../../../store/Members/action';
import { deleteMineTeamRoomAction } from '../../../../store/MineTeamRooms/action';
import { deleteSharedTeamRoomAction } from '../../../../store/SharedTeamRooms/action';

interface IOutput {
	state: HookState;
	toggleChat(item: any): void;
	closeChat(): void;
	onDeleteMine(id: string): void;
	onDeleteShared(id: string): void;
	toggleOptions(): void;
	onOpenInvites(id: string): void;
	onShare(id: string): void;
	useComponentVisible(): any;
	openDetails(): void;
}

interface HookState {
	isVisibleChat: boolean;
	isDetailsOpen: boolean;
	isChatLoading: boolean;
	isDeleting: boolean;
	isDetailVisible: boolean;
	isLoadingMembers: boolean;
	isCopied: boolean;
}

export default function useHook(): IOutput {
	const { showModal } = useGlobalModalContext();
	const {
		dispatch,
		store: { comments },
	} = useUtils();
	const { type } = comments;

	const [state, setState] = useState<HookState>({
		isVisibleChat: false,
		isChatLoading: false,
		isDeleting: false,
		isDetailVisible: false,
		isLoadingMembers: false,
		isCopied: false,
		isDetailsOpen: false,
	});

	useEffect(() => {
		if (type === GET_COMMENTS) {
			setState(prev => ({
				...prev,
				isVisibleChat: false,
			}));
		}
	}, [type]);

	const openDetails = (): void => {
		setState({ ...state, isDetailsOpen: !state.isDetailsOpen });
	};

	const useComponentVisible = () => {
		const ref = useRef<HTMLDivElement>(null);
		const handleClickOutside = (event: Event) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setState(prev => ({
					...prev,
					isDetailsOpen: false,
				}));
			}
		};
		useEffect(() => {
			document.addEventListener('click', handleClickOutside, true);
			return () => {
				document.removeEventListener('click', handleClickOutside, true);
			};
		});

		return { ref };
	};

	const toggleLoader = (field: keyof typeof state): void => {
		setState(prev => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const onDeleteMine = (_id: string): void => {
		toggleLoader('isDeleting');
		dispatch(
			deleteMineTeamRoomAction({
				_id,
				callback: () => {
					toggleLoader('isDeleting');
					toggleOptions();
				},
			})
		);
	};

	const onDeleteShared = (_id: string): void => {
		toggleLoader('isDeleting');
		dispatch(
			deleteSharedTeamRoomAction({
				_id,
				callback: () => {
					toggleLoader('isDeleting');
					toggleOptions();
				},
			})
		);
	};

	const closeChat = () => {
		toggleLoader('isVisibleChat');
	};

	const toggleChat = (item: any) => {
		toggleLoader('isChatLoading');
		dispatch(
			getCommentsAction({
				id: item._id,
				callback: () => {
					toggleLoader('isChatLoading');
					toggleLoader('isVisibleChat');
				},
			})
		);
	};

	const inviteMember = (usersIds: string[], snapId: string) => {
		dispatch(
			inviteMemberAction({
				data: { usersIds, snapId },
			})
		);
	};

	const onOpenInvites = (_id: string): void => {
		toggleLoader('isLoadingMembers');
		dispatch(
			getMembersAction({
				_id,
				callback: (item: any) => {
					toggleLoader('isLoadingMembers');
					showModal(MODAL_TYPES.INVITE_MODAL, {
						title: 'Invite team members',
						field: 'members',
						inviteMember: (usersIds: string) => inviteMember([usersIds], _id),
					});
				},
			})
		);
	};

	const onShare = (item: any): void => {
		const { link } = item;
		if (state.isCopied) return;
		setState(prev => ({ ...prev, link, isCopied: true }));
		navigator.clipboard.writeText(link);
	};

	const toggleOptions = (): void => {
		setState(prev => ({
			...prev,
			isDetailVisible: !prev.isDetailVisible,
		}));
	};

	return {
		state,
		openDetails,
		useComponentVisible,
		toggleChat,
		closeChat,
		onDeleteMine,
		onDeleteShared,
		toggleOptions,
		onOpenInvites,
		onShare,
	};
}
